import { useQuery } from "@tanstack/react-query";
import { useRoute, Link } from "wouter";
import { formatDateTime } from "../lib/utils";
import { ArrowLeft } from "lucide-react";

interface ChatDetailData {
  chat: {
    id: string;
    telegram_chat_id: string;
    username: string | null;
    first_name: string | null;
    last_name: string | null;
    status: string;
    message_count: number;
    last_message_at: string | null;
    created_at: string;
    linked_user_id: string | null;
    linked_user_email: string | null;
    linked_user_first_name: string | null;
    linked_user_last_name: string | null;
  };
  messages: Array<{
    id: string;
    chat_id: string;
    role: string;
    content: string;
    created_at: string;
  }>;
}

function getStatusBadge(status: string) {
  const map: Record<string, { label: string; className: string }> = {
    active: { label: "Активен", className: "bg-green-100 text-green-700" },
    closed: { label: "Закрыт", className: "bg-gray-100 text-gray-600" },
  };
  const item = map[status] || { label: status, className: "bg-gray-100 text-gray-600" };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${item.className}`}>
      {item.label}
    </span>
  );
}

function getDisplayName(chat: ChatDetailData["chat"]): string {
  const parts = [chat.first_name, chat.last_name].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  if (chat.username) return `@${chat.username}`;
  return chat.telegram_chat_id;
}

export default function ChatDetailPage() {
  const [, params] = useRoute("/chats/:id");
  const chatId = params?.id;

  const { data, isLoading } = useQuery<ChatDetailData>({
    queryKey: [`/api/admin/system-chats/${chatId}`],
    enabled: !!chatId,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-muted rounded w-48 animate-pulse" />
        <div className="h-64 bg-muted rounded animate-pulse" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20 text-muted-foreground">
        Чат не найден
      </div>
    );
  }

  const { chat, messages } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Link href="/chats">
          <button className="mt-1 p-1.5 rounded hover:bg-muted transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold">{getDisplayName(chat)}</h1>
          <div className="flex items-center gap-2 mt-1">
            {getStatusBadge(chat.status)}
            {chat.username && (
              <span className="text-sm text-muted-foreground">@{chat.username}</span>
            )}
          </div>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-sm text-muted-foreground">Telegram</div>
          <div className="text-sm font-medium mt-1">
            {chat.username ? `@${chat.username}` : `ID: ${chat.telegram_chat_id}`}
          </div>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-sm text-muted-foreground">Аккаунт</div>
          <div className="text-sm font-medium mt-1">
            {chat.linked_user_id ? (
              <Link href={`/users/${chat.linked_user_id}`}>
                <span className="text-primary hover:underline cursor-pointer">
                  {chat.linked_user_email}
                </span>
              </Link>
            ) : (
              <span className="text-muted-foreground">Не привязан</span>
            )}
          </div>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-sm text-muted-foreground">Сообщений</div>
          <div className="text-2xl font-bold">{chat.message_count}</div>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-sm text-muted-foreground">Последнее</div>
          <div className="text-sm font-medium mt-1">
            {chat.last_message_at ? formatDateTime(chat.last_message_at) : "—"}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="rounded-lg border border-border bg-card">
        <div className="p-4 border-b border-border">
          <h2 className="font-semibold">Переписка ({messages.length})</h2>
        </div>
        <div className="p-4 space-y-3 max-h-[600px] overflow-y-auto">
          {messages.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              Нет сообщений
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`p-3 rounded-lg ${
                  msg.role === "assistant"
                    ? "bg-primary/10 mr-12"
                    : "bg-muted ml-12"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium">
                    {msg.role === "assistant" ? "Бот" : "Пользователь"}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatDateTime(msg.created_at)}
                  </span>
                </div>
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
